import { User } from "@phosphor-icons/react"
import { Checkbox, Modal, Tag } from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { TradeKinds } from "~/constants/Kinds"

interface SelectPattiModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (selectedStps: any[]) => void
  data: any[]
  commodityName?: string
  varietyName?: string
  isLoadingData?: boolean
  type?: "storage" | "order"
}

const SelectPattiModal = ({
  isOpen,
  onClose,
  onSelect,
  data,
  commodityName,
  varietyName,
  isLoadingData = false,
  type = "storage",
}: SelectPattiModalProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  useEffect(() => {
    if (isOpen) {
      setSelectedIds([])
    }
  }, [isOpen])
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(data?.map((item: any) => item.id) || [])
    } else {
      setSelectedIds([])
    }
  }

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id])
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id))
    }
  }

  const handleOk = () => {
    const selectedStps = data.filter(item => selectedIds.includes(item.id))
    onSelect(selectedStps)
    onClose()
  }

  const COLUMNS = [
    {
      id: "select",
      Header: () => (
        <Checkbox
          checked={selectedIds.length === data.length && data.length > 0}
          onChange={checked => handleSelectAll(checked)}
        />
      ),
      Cell: ({ row }: any) => (
        <Checkbox
          checked={selectedIds.includes(row.original.id)}
          onChange={checked => handleSelectOne(row.original.id, checked)}
        />
      ),
    },
    {
      accessor: "created_at",
      Header: "Created On",
      Cell: ({ row }: any) => (
        <Cell
          title={dayjs(row.original.created_at).format("D MMM 'YY")}
          subtitle={dayjs(row.original.created_at).format("h:mm a")}
        />
      ),
    },
    {
      accessor: "id",
      Header: type === "storage" ? "Storage Patti's" : "Order Patti's",
      Cell: ({ row }: any) => (
        <Cell
          titleClassName="text-brown-400"
          title={
            <Tag
              intent="info"
              variant="minimal"
              className="!bg-brown-400 !text-white !rounded-sm"
            >
              {`#${row.original.id}` || "N/A"}
            </Tag>
          }
        />
      ),
    },
    ...(type === "order"
      ? [
          {
            accessor: "seller",
            Header: "Supplier",
            Cell: ({ row }: any) => (
              <Cell
                title={row.original.seller?.user?.[0]?.full_name || "N/A"}
                subtitle={
                  <Tag
                    leftIcon={<User size={14} weight="fill" />}
                    intent="info"
                    variant="minimal"
                    className="!rounded-xl"
                  >
                    {row.original.seller?.user?.[0]?.external_reference_id ||
                      "N/A"}
                  </Tag>
                }
              />
            ),
          },
        ]
      : []),
    {
      accessor: "buyer",
      Header: "Buyer",
      Cell: ({ row }: any) => (
        <Cell
          title={row.original.buyer?.user?.[0]?.full_name || "N/A"}
          subtitle={
            <Tag
              leftIcon={<User size={14} weight="fill" />}
              intent="info"
              variant="minimal"
              className="!rounded-xl"
            >
              {row.original.buyer?.user?.[0]?.external_reference_id || "N/A"}
            </Tag>
          }
        />
      ),
    },
    {
      accessor: "commodity_variety",
      Header: type === "order" ? "Commodity" : "Variety",
      Cell: ({ row }: any) => (
        <Cell title={row.original.commodity_variety?.name || "N/A"} />
      ),
    },
    {
      accessor: "grade",
      Header: "Grade",
      Cell: ({ row }: any) => (
        <Cell
          title={
            <Tag intent="info" className="!rounded-xl" variant="minimal">
              {row.original.trade_items?.[0]?.variety_grade?.name || "N/A"}
            </Tag>
          }
        />
      ),
    },
    {
      Header: "Rate",
      accessor: "item_units",
      Cell({ value, row: { original } }: any) {
        if (!value) return "--"
        return (
          <Cell
            titleClassName="!text-brown-400"
            title={`${inr(
              original.trade_items_aggregate?.aggregate?.avg?.rate || 0
            )} / ${value[0]?.rate_unit?.name || "na"}`}
          />
        )
      },
    },
    {
      accessor: "total_quantity",
      Header: type === "order" ? "Qty" : "Actual Qty",
      Cell: ({ row }: any) => (
        <Cell
          titleClassName="text-brown-400"
          title={`${row.original.total_quantity || "N/A"} kg`}
        />
      ),
    },
    ...(type === "storage"
      ? [
          {
            id: "used_quantity",
            Header: "Used Qty",
            Cell: ({ row: { original } }: any) => {
              const total_quantity = original.total_quantity
              const remaining = original.deducted_quantity_remaining || 0
              const used_quantity = total_quantity - remaining
              return (
                <Cell
                  titleClassName="text-brown-400"
                  title={used_quantity || "--"}
                />
              )
            },
          },
          {
            Header: "Remaining Quantity",
            accessor: "deducted_quantity_remaining",
            id: "remaining_quantity",
            Cell({ value }: any) {
              return (
                <Cell titleClassName="!text-brown-400" title={`${value} kg`} />
              )
            },
          },
        ]
      : []),
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Patti"
      bodyClassName="max-w-7xl"
      primaryActionButtonProps={{
        text: "Ok",
        onClick: handleOk,
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        onClick: onClose,
      }}
    >
      {commodityName && varietyName && (
        <div className="bg-blue-50 p-2 mb-4 text-sm font-semibold text-blue-900 rounded">
          {commodityName} | {varietyName}
        </div>
      )}
      <TableView isLoading={isLoadingData} columns={COLUMNS} data={data} />
    </Modal>
  )
}

export default SelectPattiModal
